import { FC, useEffect, useRef, useContext, useState, useMemo } from 'react'

import TranslationPlayer from '../translation-player/translation-player'
import TranslationSoundButton from '../translation-sound-button/translation-sound-button'
import statisticContext from 'context/statisticsContext'
import WebinarSocketContext from 'context/webinarSocketContext'
import PlayerControls from '../controls/player-controls'
import useFullscreen from '../translation-hooks/useFullscreen'
import usePCWindow from 'hooks/usePCWindow'
import useDeviceOrientation, { ScreenOrientation } from 'hooks/useDeviceOrientation'
import useIsHideControls from '../translation-hooks/useIsHideControls'
import useVisibleUnmute from '../useVisibleUnmute'

import { checkJsonString, extractIdAndKey } from 'utils/rutube/rutubeVideoUtils'
import { IAnyObj } from 'constants/types/common.types'
import { PlayerStatus, statisticEvent } from '../../data'
import { StatusVocabulary } from 'constants/data'
import {
  CONTROLS_SHOW_TIME,
  CONTROLS_FIRST_SHOW_TIME,
  CommandMessage,
  MessageType,
  PlayerState,
} from './rutube-translation-constants'

import styles from '../translation-player.module.scss'
import iframeStyle from '../../iframe.module.scss'

interface IRutubeVideo {
  videoLink: string
}

const RutubeTranslation: FC<IRutubeVideo> = ({ videoLink }) => {
  const isPC = usePCWindow()
  const orientation = useDeviceOrientation()
  const socket = useContext(statisticContext)
  const { webinarStatus } = useContext(WebinarSocketContext)

  const [isMuted, setIsMuted] = useState(true)
  const [isVideoStarted, setIsVideoStarted] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [isVideoBug, setIsVideoBug] = useState(false)

  const playerRef = useRef<HTMLIFrameElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const fakeContainerRef = useRef<HTMLDivElement>(null)
  const startVideoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const doCommand = (type: string, data?: IAnyObj) => {
    const playerContentWindow = playerRef?.current?.contentWindow

    playerContentWindow?.postMessage(JSON.stringify({ type, data: data || {} }), '*')
  }

  const playVideo = () => doCommand(CommandMessage.PLAY)

  const setVolume = (value: number) => {
    doCommand(CommandMessage.SET_VOLUME, { volume: value / 100 })
  }

  const unMute = () => {
    playVideo()
    setVolume(100)
    setIsMuted(false)
  }

  const mute = () => {
    doCommand(CommandMessage.MUTE)
    setIsMuted(true)
  }

  const handleClickToggle = () => (isMuted ? unMute() : mute())

  const getRutubeWatch = (event: MessageEvent) => {
    if (!checkJsonString(event.data)) return

    const message = JSON.parse(event.data)

    if (message.type === MessageType.READY) {
      mute()
      setIsReady(true)
      startVideoTimeoutRef.current = setTimeout(playVideo, 500)
    }

    if (message.type === MessageType.CHANGE_STATE) {
      if (message.data.state === PlayerState.PLAYING) {
        setIsVideoStarted(true)
        setIsVideoBug(false)
        socket?.sendEvent(statisticEvent[PlayerStatus.PLAYING])
      }

      if (message.data.state === PlayerState.STOPPED) {
        socket?.sendEvent(statisticEvent[PlayerStatus.END])
      }

      if (message.data.state === PlayerState.PAUSED) {
        playVideo()
      }
    }

    if (message.type === MessageType.ERROR) {
      setIsVideoBug(true)
    }
  }

  useEffect(() => {
    window.addEventListener('message', getRutubeWatch)

    return () => {
      if (startVideoTimeoutRef.current !== null) {
        clearTimeout(startVideoTimeoutRef.current)
        startVideoTimeoutRef.current = null
      }
      window.removeEventListener('message', getRutubeWatch)
    }
  }, [])

  const { isFullScreen, dimension, toggleFullScreen } = useFullscreen(containerRef)
  const isHideControls = useIsHideControls(
    fakeContainerRef,
    CONTROLS_SHOW_TIME,
    CONTROLS_FIRST_SHOW_TIME,
    true
  )
  useVisibleUnmute(!isVideoStarted, unMute)

  const { rutubeVideoId, rutubePrivatkey } = useMemo(() => extractIdAndKey(videoLink), [videoLink])
  const isWebinarStarted = webinarStatus === StatusVocabulary.ACTIVE
  const isVerticalOrientation = orientation === ScreenOrientation.vertical

  return (
    <TranslationPlayer
      isFullScreen={isFullScreen}
      dimension={dimension}
      isPC={isPC}
      isVerticalOrientation={isVerticalOrientation}
      fakeContainerRef={fakeContainerRef}
      containerRef={containerRef}
    >
      {!isVideoBug && (
        <>
          <iframe
            width="100%"
            height="100%"
            src={`https://rutube.ru/play/embed/${rutubeVideoId}/?p=${rutubePrivatkey}`}
            className={iframeStyle.playerIframe}
            ref={playerRef}
            id="video_frame"
            allow="autoplay; screen-wake-lock"
          />
          <div className={styles.hideFrame} />
        </>
      )}
      {isReady && !isVideoBug && (
        <PlayerControls
          isHideControls={isHideControls}
          isFullScreen={isFullScreen}
          toggleFullScreen={toggleFullScreen}
          isMuted={isMuted}
          toggleMute={handleClickToggle}
          setVolume={setVolume}
          isNotTransparent
        />
      )}
      {isReady && isWebinarStarted && (isMuted || !isVideoStarted) && (
        <TranslationSoundButton onClick={handleClickToggle} isPaused={isVideoStarted} />
      )}
    </TranslationPlayer>
  )
}

export default RutubeTranslation
